"use client";

import type { ReactNode } from "react";
import {
  Checkbox,
  DataTable,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT, LABEL } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormatter } from "@/lib/tool-kit/research-design/format";
import type { ParsedTable } from "@/lib/tool-kit/research-design/matrix";

/*
 * The paste box the research-design widgets share: a monospace area for a spreadsheet
 * paste, example and clear actions, the header-row and ID-column switches (pre-set to
 * what detection guessed), and a collapsible view of the table exactly as it was read.
 * Every word comes from the widget's own strings, through `strings`.
 */

/** The keys a widget's strings must declare to use the paste box. */
export interface DataInputStrings {
  dataLabel: string;
  dataHint: string;
  dataPlaceholder: string;
  loadExample: string;
  clear: string;
  firstRowHeader: string;
  firstColumnIds: string;
  showParsed: string;
  previewNote: string;
  idHeader: string;
}

const PREVIEW_ROWS = 6;

export function DataInput({
  id,
  strings,
  fmt,
  value,
  onChange,
  onReset,
  example,
  table,
  columnNames,
  onHeader,
  onIds,
  summary,
  children,
}: {
  id: string;
  strings: DataInputStrings;
  fmt: NumberFormatter;
  value: string;
  onChange: (value: string) => void;
  /** Replace the text and forget any header/ID override. */
  onReset: (value: string) => void;
  example: string;
  table: ParsedTable;
  /** Display names of the data columns, as the results will use them. */
  columnNames: readonly string[];
  onHeader: (value: boolean) => void;
  onIds: (value: boolean) => void;
  /** "Read as 12 respondents × 5 items" — shown once there is data. */
  summary: ReactNode;
  /** Extra controls between the switches and the preview. */
  children?: ReactNode;
}) {
  const hasData = table.body.length > 0 && columnNames.length > 0;
  const head = [
    ...(table.ids ? [table.idName ?? strings.idHeader] : []),
    ...columnNames,
  ];
  const rows = table.body
    .slice(0, PREVIEW_ROWS)
    .map((row, i) => [
      ...(table.ids ? [table.ids[i] ?? ""] : []),
      ...columnNames.map((_, column) => row[column] ?? ""),
    ]);
  return (
    <section className="space-y-3">
      <TextAreaField
        hint={strings.dataHint}
        id={id}
        label={strings.dataLabel}
        onChange={onChange}
        placeholder={strings.dataPlaceholder}
        rows={8}
        value={value}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => onReset(example)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={value === ""}
          onClick={() => onReset("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Checkbox
          checked={table.layout.header}
          label={strings.firstRowHeader}
          onChange={onHeader}
        />
        <Checkbox
          checked={table.layout.ids}
          label={strings.firstColumnIds}
          onChange={onIds}
        />
      </div>
      {children}
      {hasData ? (
        <>
          <p className={HINT}>{summary}</p>
          <details className="space-y-2">
            <summary className={`${LABEL} cursor-pointer`}>
              {strings.showParsed}
            </summary>
            <DataTable head={head} rows={rows} />
            {table.body.length > PREVIEW_ROWS ? (
              <p className={HINT}>
                {t(strings.previewNote, {
                  shown: fmt(PREVIEW_ROWS),
                  total: fmt(table.body.length),
                })}
              </p>
            ) : null}
          </details>
        </>
      ) : null}
    </section>
  );
}
